/**
 * @module services/logout
 *
 * Logout service that handles the complete logout flow:
 * 1. Closes any open confirmation dialog
 * 2. Invalidates the refresh token on the server (if using JWT)
 * 3. Clears all authentication state (reactive refs + localStorage)
 * 4. Clears all media player session data
 * 5. Wipes all localStorage and sessionStorage
 * 6. Removes auth headers from the Axios instance
 * 7. Navigates to the login page
 */

import { api } from "@/api/config.js";
import router from "@/router";
import { useLogin } from "@/composables/useLogin";
import { useMediaPlayer } from "@/composables/useMediaPlayer";

/**
 * Perform a full logout: server-side token invalidation, client-side
 * state cleanup, and navigation to the login page.
 *
 * Server-side token invalidation errors are intentionally ignored
 * because the user is logging out regardless -- the token will
 * expire naturally even if the server call fails.
 */
export const logout = async () => {
  const { logout: clearLoginState, isJwtAuth, getRefreshToken } = useLogin();

  // If using JWT auth, tell the server to invalidate the refresh token
  // so it cannot be reused by anyone who may have captured it
  if (isJwtAuth()) {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      try {
        await api.post("/auth/logout", { refreshToken });
      } catch (error) {
        // Ignore errors - we're logging out anyway and the token will expire
        console.warn("Failed to invalidate refresh token:", error.message);
      }
    }
  }

  // Clear authentication state in both reactive refs and localStorage
  clearLoginState();

  // Clear all media player data (episode positions, preferences, etc.)
  const mediaPlayer = useMediaPlayer();
  mediaPlayer.clearAllData();

  // Wipe all remaining app data from browser storage
  localStorage.clear();
  sessionStorage.clear();

  // Remove the Authorization header from the Axios instance so
  // subsequent requests (e.g., the login page) don't send stale tokens
  delete api.defaults.headers.common["Authorization"];

  // Navigate to the login page
  router.push({ name: "login" });
};
